import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  boardFromBytes,
  boardToBytes,
  type Board,
  type InputControl,
} from "../lib/game";

const INPUTS: readonly InputControl[] = [
  "left",
  "right",
  "softDrop",
  "hardDrop",
  "rotateCW",
  "rotateCCW",
];

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function base64UrlToBytes(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]*$/u.test(value)) throw new Error("Invalid base64url payload");
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(value.replaceAll("-", "+").replaceAll("_", "/") + padding);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

/** A one-event input batch: low 3 bits are the control and bit 3 is held. */
export function packInput(control: InputControl, held: boolean): string {
  const index = INPUTS.indexOf(control);
  if (index < 0) throw new RangeError(`Unknown input control: ${control}`);
  return bytesToBase64Url(Uint8Array.of(index | (held ? 0b1000 : 0)));
}

export function unpackInput(value: string): { control: InputControl; held: boolean } {
  const bytes = base64UrlToBytes(value);
  if (bytes.length !== 1 || (bytes[0] & 0b1111_0000) !== 0) {
    throw new Error("Invalid input batch payload");
  }
  const control = INPUTS[bytes[0] & 0b111];
  if (!control) throw new Error("Unknown input control in batch");
  return { control, held: (bytes[0] & 0b1000) !== 0 };
}

export function flattenBoard(board: Board): number[] {
  return Array.from(boardToBytes(board));
}

export function expandBoard(flat: readonly number[]): Board {
  if (flat.length !== BOARD_WIDTH * BOARD_HEIGHT) {
    throw new RangeError("A committed board must contain exactly 240 cells");
  }
  return boardFromBytes(Uint8Array.from(flat));
}

export function encodeUtf8(value: string): string {
  return bytesToBase64Url(new TextEncoder().encode(value));
}

export function decodeUtf8(value: string): string {
  return new TextDecoder("utf-8", { fatal: true }).decode(base64UrlToBytes(value));
}
